import { Box, Typography } from "@mui/material";
import type * as React from "react";
import CologuardLogo from "assets/images/cologuard/cologuard-logo.svg";
import GlContentTag from "components/v3/sharedComponents/GlContentTag";
import { COLORS } from "components/v3/Theme/colors";

const ComparisonSection: React.FC = () => (
  <Box
    component="section"
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: { md: 4, xs: 3 },
      width: "100%"
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        textAlign: "center",
        marginTop: 5,
        marginBottom: 4
      }}
    >
      <Typography component="h2" variant="body_strong_xl_black" sx={{ m: 0 }}>
        Two options to consider
      </Typography>
      <Typography
        component="p"
        variant="body_black"
        sx={{ m: "0 auto", maxWidth: 1000 }}
      >
        There are several different options for colon cancer screening. Two of
        these options include{" "}
        <Box component="span" sx={{ color: COLORS.PRIMARY, fontWeight: 700 }}>
          colonoscopy
        </Box>{" "}
        and the{" "}
        <Box component="span" sx={{ color: COLORS.PRIMARY, fontWeight: 700 }}>
          Cologuard®
        </Box>{" "}
        test .
      </Typography>
    </Box>

    <Box
      sx={{
        display: "grid",
        gap: 3,
        gridTemplateColumns: { lg: "repeat(2, 1fr)", xs: "1fr" }
      }}
    >
      <Box
        sx={{
          bgcolor: COLORS.WHITE,
          borderRadius: "16px",
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: { md: 4, xs: 3 }
        }}
      >
        <Typography component="h3" variant="body_strong_lg_black" sx={{ m: 0 }}>
          What&apos;s a colonoscopy?
        </Typography>
        <Typography component="p" variant="body_black" sx={{ m: 0 }}>
          A{" "}
          <Box component="span" sx={{ color: COLORS.PRIMARY, fontWeight: 700 }}>
            colonoscopy
          </Box>{" "}
          is considered the gold standard for colon cancer screening because it
          can find cancer (and precancer) and remove it. A doctor uses a small
          camera to look inside the colon and rectum. If small growths called
          polyps are found, they can usually be removed during the procedure,
          before they become cancer.
        </Typography>
        <Typography
          component="p"
          variant="body_black"
          sx={{ m: 0, mt: "auto" }}
        >
          <GlContentTag sx={{ mr: 1 }} variant="important" />
          This test requires a special diet for a short time, medications for
          bowel cleansing, and a sedative. Most people have to take time off
          work and arrange for someone else to drive them to and from the
          appointment. Your healthcare provider can help schedule a colonoscopy
          if this is your preferred screening method.
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: COLORS.WHITE,
          borderRadius: "16px",
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: { md: 4, xs: 3 }
        }}
      >
        <Box
          sx={{
            flex: 1,
            "&::after": {
              clear: "both",
              content: '""',
              display: "table"
            }
          }}
        >
          <Box
            alt="Cologuard by Exact Sciences"
            component="img"
            src={CologuardLogo}
            sx={{
              float: "right",
              height: "auto",
              marginBottom: "15px",
              marginLeft: "36px",
              objectFit: "contain",
              width: 210,
              "@media (max-width: 550px)": {
                display: "block",
                float: "none",
                marginLeft: 0
              }
            }}
          />
          <Typography
            component="h3"
            variant="body_strong_lg_black"
            sx={{ m: 0 }}
          >
            What&apos;s Cologuard?
          </Typography>
          <Typography component="p" variant="body_black" sx={{ m: 0, mt: 2 }}>
            <Box
              component="span"
              sx={{ color: COLORS.PRIMARY, fontWeight: 700 }}
            >
              Cologuard
            </Box>{" "}
            is a non-invasive screening option that uses a stool sample, which
            can be collected at home.* No preparation or time off work is
            required. If you get a positive result, it’s important to get a
            colonoscopy after discussing your results with your healthcare
            provider.
          </Typography>
        </Box>
        <Typography
          component="p"
          variant="body_black"
          sx={{ m: 0, mt: "auto" }}
        >
          If Cologuard sounds like it could be an option for you, review the
          information below to learn more and get started.
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default ComparisonSection;
